import { CheckBoxWidget, FlyoutListMenuWidget, MenuItemInfo } from '@tableau/widgets';
import { ArrayRowStore, BodyCell, DataGrid, HeaderColumn } from '@tableau/widgets-datagrid';
import React from 'react';

import { SortableHeader } from '~commonInfra/components/dataGrid/SortableHeader';
import { Icons } from '~commonInfra/components/Icons';
import { Messages } from '~src/codegen/Localize';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { ExplorerStyles } from '~src/spacesPlatform/components/Explorer/ExplorerStyles';
import { ExplorerDropdown } from '~src/spacesPlatform/components/Explorer/widgets/ExplorerDropdown';
import { ExplorerFlyoutPanelProps } from '~src/spacesPlatform/components/Explorer/widgets/ExplorerFlyoutPanel';
import { SpaceHeaderStyles } from '~src/spacesPlatform/components/header/SpaceHeaderStyles';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { classes } from '~src/style';

import { ContentRelayPlanDetailsStyles as Styles } from './ContentRelayPlanDetailsStyles';
import { IPlanContentRow, PlanContentType } from './planContentsService';

export const ContentRelayPlanContentsGridTestIds = {
  Grid: 'relay-plan-contents-grid',
  SelectAll: 'relay-plan-contents-select-all',
  Select: 'relay-plan-contents-select-',
  Name: 'relay-plan-contents-name',
  Unavailable: 'relay-plan-contents-unavailable',
  NotDownloadable: 'relay-plan-contents-not-downloadable',
  RowActions: 'relay-plan-contents-actions-',
};

export type ContentsSortKey = 'name' | 'type' | 'owner' | 'location' | 'modified';

export interface IContentsSort {
  readonly key: ContentsSortKey;
  readonly ascending: boolean;
}

/** Matches the mockup: the grid opens grouped by project. */
export const DEFAULT_CONTENTS_SORT: IContentsSort = { key: 'location', ascending: true };

export interface ContentRelayPlanContentsGridProps {
  readonly rows: ReadonlyArray<IPlanContentRow>;
  readonly urls: SpacesSiteUrls;
  readonly selectedKeys: ReadonlyArray<string>;
  readonly onSelectionChange: (keys: ReadonlyArray<string>) => void;
  /** Removes the given rows (by key) from the plan. Absent for viewers who can't edit the plan. */
  readonly onRemove?: (keys: ReadonlyArray<string>) => void;
}

interface IContentsGridContext {
  readonly props: ContentRelayPlanContentsGridProps;
  readonly allSelected: boolean;
  readonly toggleAll: () => void;
  readonly toggleRow: (key: string) => void;
}

const ContentsGridContext = React.createContext<IContentsGridContext>(undefined);

function typeLabel(contentType: PlanContentType): string {
  switch (contentType) {
    case 'workbook': return Messages.workbook();
    case 'datasource': return Messages.datasource();
    default: return Messages.project();
  }
}

function itemUrl(row: IPlanContentRow, urls: SpacesSiteUrls): string {
  switch (row.contentType) {
    case 'workbook': return urls.workbookUrl(row.id);
    case 'datasource': return urls.datasourceUrl(row.id);
    default: return urls.projectUrl(row.id);
  }
}

function sortValue(row: IPlanContentRow, key: ContentsSortKey): string | number {
  switch (key) {
    case 'type': return typeLabel(row.contentType);
    case 'owner': return row.owner && row.owner.displayName ? row.owner.displayName : '';
    case 'location': return row.location || '';
    case 'modified': return row.updatedAt ? new Date(row.updatedAt).getTime() : 0;
    default: return row.name || '';
  }
}

/** Sorts rows by the given column; ties fall back to name so the order is stable. */
export function sortContentRows(rows: ReadonlyArray<IPlanContentRow>, sort: IContentsSort): IPlanContentRow[] {
  const direction = sort.ascending ? 1 : -1;
  const compare = function(a: string | number, b: string | number): number {
    if (typeof a === 'number' && typeof b === 'number') {
      return a - b;
    }
    return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
  };
  return rows.slice().sort(function(a, b) {
    const primary = compare(sortValue(a, sort.key), sortValue(b, sort.key));
    return primary !== 0 ? direction * primary : compare(a.name || '', b.name || '');
  });
}

/** The name link, then a grey or warning note on the same line (not downloadable / related content). */
function NameCell(props: { row: IPlanContentRow }): JSX.Element {
  const context = React.useContext(ContentsGridContext);
  const row = props.row;
  if (row.unavailable) {
    return (
      <span className={Styles.Unavailable} data-tb-test-id={ContentRelayPlanContentsGridTestIds.Unavailable}>
        {row.name || Messages.relayPlanContentsUnavailable()}
        {row.name ? ' (' + Messages.relayPlanContentsUnavailable() + ')' : null}
      </span>
    );
  }
  let note: JSX.Element = null;
  if (!row.downloadable) {
    // The server resolved it but won't download it, so it's listed yet excluded from the relay.
    note = (
      <span className={Styles.MissingNote} data-tb-test-id={ContentRelayPlanContentsGridTestIds.NotDownloadable}>
        {Messages.relayPlanContentsNotDownloadable()}
      </span>
    );
  } else if (!row.inSelection) {
    note = <span className={Styles.NameNote}>{Messages.relayPlanContentsRelatedItem()}</span>;
  }
  return (
    <React.Fragment>
      {/* The grid manages focus, so the link isn't a tab stop (as the list views' LinkCell). */}
      <a className={Styles.NameText} href={itemUrl(row, context.props.urls)} tabIndex={-1} title={row.name} data-tb-test-id={ContentRelayPlanContentsGridTestIds.Name}>
        {row.name}
      </a>
      {note}
    </React.Fragment>
  );
}

function SelectCell(props: { row: IPlanContentRow }): JSX.Element {
  const context = React.useContext(ContentsGridContext);
  const row = props.row;
  return (
    <CheckBoxWidget
      checked={context.props.selectedKeys.indexOf(row.key) !== -1}
      handleChange={function() { context.toggleRow(row.key); }}
      removeLeftMargin={true}
      tabIndex={-1}
      aria-label={Messages.relayPlanContentsSelectItem({ NAME: row.name || row.id })}
      testId={ContentRelayPlanContentsGridTestIds.Select + row.key}
    />
  );
}

/**
 * Row ⋯ menu. Pilot scope is just "Remove from plan" (no mapping rules yet), which only applies to items
 * the plan's selection lists itself — related content comes and goes with the item that pulls it in.
 */
function RowActions(props: { row: IPlanContentRow }): JSX.Element {
  const context = React.useContext(ContentsGridContext);
  const onRemove = context.props.onRemove;
  const row = props.row;
  if (!onRemove || !row.inSelection) {
    return null;
  }
  const menuItems: MenuItemInfo[] = [{ displayValue: Messages.relayPlanContentsRemove(), value: 'remove' }];
  const label = Messages.relayPlanContentsRowActions({ NAME: row.name || row.id });
  const renderPanel = function(panelProps: ExplorerFlyoutPanelProps): JSX.Element {
    return (
      <FlyoutListMenuWidget
        role={'menu'}
        anchorEl={panelProps.anchorEl}
        allowAutoPosition={true}
        menuItemInfos={menuItems}
        onSelect={function() {
          panelProps.onCloseRequest();
          onRemove([row.key]);
        }}
        requestCloseAll={panelProps.onCloseRequest}
        testId={panelProps.testId + '-list'}
        toggleEl={panelProps.anchorEl}
      />
    );
  };
  return (
    <ExplorerDropdown
      buttonTitle={label}
      panelTitle={label}
      testId={ContentRelayPlanContentsGridTestIds.RowActions + row.key}
      renderButtonContentOnly={true}
      renderButtonContent={function(showingOrActive: boolean) {
        return <Icons.actionIcon hovered={showingOrActive} style={{ verticalAlign: 'middle' }} />;
      }}
      renderPanel={renderPanel}
      buttonStyleOverrides={SpaceHeaderStyles.IconButtonStyleOverridesV2}
    />
  );
}

function cellContent(col: string, row: IPlanContentRow, urls: SpacesSiteUrls): React.ReactNode {
  switch (col) {
    case 'select': return <SelectCell row={row} />;
    case 'name': return <NameCell row={row} />;
    case 'actions': return <RowActions row={row} />;
    case 'type': return typeLabel(row.contentType);
    case 'owner':
      return row.owner && row.owner.displayName
        ? <a href={urls.userUrl(row.owner.username, row.owner.domainName)} tabIndex={-1}>{row.owner.displayName}</a>
        : null;
    case 'location': return row.location;
    case 'modified': return row.updatedAt ? L10n.localizedDateTime(new Date(row.updatedAt)) : null;
    default: return null;
  }
}

function ContentsCell(props: BodyCell.CellFormatterProps): JSX.Element {
  const context = React.useContext(ContentsGridContext);
  const row = props.row.data as IPlanContentRow;
  return <div className={Styles.Cell}>{cellContent(props.col.name, row, context.props.urls)}</div>;
}

/** A sortable header, as in the list view: the sort arrow takes the left padding; hover underlines. */
function SortHeaderCell(props: HeaderColumn.HeaderFormatterProps): JSX.Element {
  return (
    <div className={Styles.HeaderCell} style={{ textDecoration: props.hovered ? 'underline' : 'none' }}>
      <SortableHeader.ArrowIcon sorted={props.sorted} ascending={props.sortAscending} />
      {props.col.headerText}
    </div>
  );
}

function PlainHeaderCell(props: HeaderColumn.HeaderFormatterProps): JSX.Element {
  return <div className={Styles.HeaderCell}>{props.col.headerText}</div>;
}

function SelectAllHeaderCell(): JSX.Element {
  const context = React.useContext(ContentsGridContext);
  return (
    <div className={Styles.Cell}>
      <CheckBoxWidget
        checked={context.allSelected}
        handleChange={context.toggleAll}
        removeLeftMargin={true}
        tabIndex={-1}
        aria-label={Messages.relayPlanContentsSelectAll()}
        testId={ContentRelayPlanContentsGridTestIds.SelectAll}
      />
    </div>
  );
}

function column(name: string, headerText: string, preferredWidth: number, sortable: boolean): DataGrid.Column {
  return {
    name: name,
    dataKey: name,
    headerText: headerText,
    cellComponent: ContentsCell,
    headerComponent: sortable ? SortHeaderCell : PlainHeaderCell,
    sortable: sortable,
    preferredWidth: preferredWidth,
  };
}

function buildColumns(): DataGrid.Column[] {
  // Mockup column order. Connection is out of pilot scope; Modified covers the story's "last modified".
  return [
    { ...column('select', '', 48, false), headerComponent: SelectAllHeaderCell, resizable: false },
    { ...column('name', Messages.name(), undefined, true), growToFit: true },
    { ...column('actions', Messages.actions(), 72, false), resizable: false },
    column('type', Messages.type(), 120, true),
    column('owner', Messages.owner(), 200, true),
    column('location', Messages.location(), 220, true),
    column('modified', Messages.modified(), 200, true),
  ];
}

/**
 * The plan's extraction preview on the Overview tab: one row per item the plan resolves to, in the
 * shared DataGrid as the list views use it — sortable (Location ascending by default), with checkboxes
 * feeding the bulk "Remove from plan". A workbook whose published data sources aren't in the plan
 * carries a warning note; an in-plan data source notes the plan workbooks that use it.
 */
export function ContentRelayPlanContentsGrid(props: ContentRelayPlanContentsGridProps): JSX.Element {
  const [sort, setSort] = React.useState<IContentsSort>(DEFAULT_CONTENTS_SORT);
  const cols = React.useMemo(buildColumns, []);
  const rows = React.useMemo(function() { return sortContentRows(props.rows, sort); }, [props.rows, sort]);

  const allSelected = rows.length > 0 && rows.every(function(row) { return props.selectedKeys.indexOf(row.key) !== -1; });
  const context: IContentsGridContext = {
    props: props,
    allSelected: allSelected,
    toggleAll: function() {
      props.onSelectionChange(allSelected ? [] : rows.map(function(row) { return row.key; }));
    },
    toggleRow: function(key: string) {
      props.onSelectionChange(props.selectedKeys.indexOf(key) === -1
        ? props.selectedKeys.concat([key])
        : props.selectedKeys.filter(function(k) { return k !== key; }));
    },
  };

  const onHeaderClick = function(_rowData: null, cell: DataGrid.Cell): void {
    const col = cols[cell.colIdx];
    if (!col || !col.sortable) {
      return;
    }
    const key = col.dataKey as ContentsSortKey;
    setSort(sort.key === key ? { key: key, ascending: !sort.ascending } : { key: key, ascending: true });
  };

  const rowStore = React.useMemo(function() {
    return new ArrayRowStore(rows.map(function(row): DataGrid.Row { return { key: row.key, data: { ...row } }; }));
  }, [rows]);

  return (
    <ContentsGridContext.Provider value={context}>
      <div
        className={classes('tb-spaces-list-view', ExplorerStyles.ListView, ExplorerStyles.DataGridBodyTopBorder, Styles.GridRegion)}
        data-tb-test-id={ContentRelayPlanContentsGridTestIds.Grid}
      >
        <DataGrid
          cols={cols}
          rowStore={rowStore}
          config={{
            colsPerChunk: cols.length,
            rowOuterHeight: ExplorerStyles.LIST_VIEW_ROW_OUTER_HEIGHT,
            rememberLastScrollPos: true,
          }}
          sortKey={sort.key}
          sortAscending={sort.ascending}
          onHeaderClick={onHeaderClick}
          showVerticalGridLines={false}
          showHorizontalGridLines={true}
          showRowBanding={false}
          aria-label={Messages.relayPlanContentsGridLabel()}
        />
      </div>
    </ContentsGridContext.Provider>
  );
}
